import axios from 'axios';
import * as Sentry from '@sentry/react';
import toast from 'react-hot-toast';
import env from './env';

const API_BASE_URL = env.API_URL;

const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token & performance timestamp
api.interceptors.request.use(
    (config) => {
        config.metadata = { startTime: performance.now() };
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

/** Paths where 401/403 are expected (wrong password, pending approval) — do not wipe session or hard-redirect */
function isPublicAuthRequest(config) {
    const u = `${config?.baseURL || ''}${config?.url || ''}`.toLowerCase();
    return (
        u.includes('auth/login') ||
        u.includes('auth/register') ||
        u.includes('auth/forgot-password') ||
        u.includes('auth/reset-password') ||
        u.includes('auth/refresh')
    );
}

// Response interceptor: auth handling, global toast on error, Sentry for 5xx, performance telemetry
api.interceptors.response.use(
    (response) => {
        if (response.config?.metadata?.startTime) {
            const durationMs = Math.round(performance.now() - response.config.metadata.startTime);
            const rawLength = response.headers['content-length'] || JSON.stringify(response.data || '').length;
            window.__PERF_METRICS__ = window.__PERF_METRICS__ || [];
            window.__PERF_METRICS__.push({
                url: response.config.url,
                method: response.config.method,
                status: response.status,
                durationMs,
                bytes: Number(rawLength) || 0,
                timestamp: Date.now()
            });
        }
        return response;
    },
    (error) => {
        const status = error.response?.status;
        const message = error.response?.data?.message || error.response?.data?.error?.message || 'Something went wrong';
        const publicAuth = isPublicAuthRequest(error.config);

        if (status && ![401, 403, 404].includes(status)) {
            Sentry.captureException(error, {
                tags: { api: true, status, endpoint: error.config?.url },
                extra: { method: error.config?.method, response: error.response?.data },
            });
        }

        if (status === 401) {
            if (publicAuth) {
                return Promise.reject(error);
            }
            const userJson = localStorage.getItem('user');
            let parsedUser = null;
            try {
                parsedUser = userJson ? JSON.parse(userJson) : null;
            } catch (_) {}
            const isSalesExecutive = parsedUser?.role === 'sales_executive';
            localStorage.removeItem('token');
            if (!isSalesExecutive) {
                localStorage.removeItem('user');
                window.location.href = '/login';
            } else {
                toast.error('Session expired. Please log in again.');
            }
            return Promise.reject(error);
        }

        if (status === 403) {
            if (!publicAuth) {
                toast.error(message || 'Access denied');
            }
        } else if (status === 429) {
            // Location throttle: skip toast for /locations/log so sales execs don't see "too frequent"
            const isLocationLog = error.config?.url?.includes('/locations/log');
            if (!isLocationLog) {
                toast.error(message || 'Too many requests. Please try again later.');
            }
        } else if (status && status >= 500) {
            toast.error('Server error. Please try again.');
        } else if (message && status !== 404) {
            toast.error(message);
        }

        return Promise.reject(error);
    }
);

export default api;
